import { StyleSheet } from 'aphrodite';
import { THEME } from '../../../components-v2/Theme/theme';
import {
	aceGray,
	brandPrimary,
	brandPrimaryActive,
	brandPrimaryText,
	brandSecondary,
	darkGrayFontColor,
	gray3,
	inputBorderColor,
	midGrey,
	success,
	titles,
	white,
} from '../../../styles/colors';
import { shadowMd, shadowSm } from '../../../styles/shadows';
import { cardStyle, CssSelectors, Layouts } from '../../../styles/styles';

const {
	common: { borderRadius },
	spacing: { xs },
} = THEME;

export const styleSheet = StyleSheet.create({
	summaryContainer: {
		height: 72,
		overflow: 'hidden',
		// Establishes a containing block for position:fixed descendants so browser-injected
		// divs (e.g. from extensions saved into template HTML) can't escape to the viewport.
		transform: 'translateZ(0)',
	},
	expiryBadge: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		backgroundColor: '#fe9a00',
		color: white,
		fontSize: 10,
		fontWeight: 600,
		padding: `4px 8px`,
		boxShadow: shadowMd,
		borderRadius: 'calc(infinity * 1px)',
		cursor: 'pointer',
		gap: 4,
		':has(*) > svg': {
			display: 'block',
			width: 10,
			height: 10,
		},
	},
	topCorner: {
		position: 'absolute',
		top: -8,
		right: -8,
		zIndex: 1,
		display: 'flex',
		gap: 6,
	},
	body: {
		background: 'transparent !important',
		color: gray3,
		fontFamily: 'Sora !important',
		fontSize: '14px !important',
	},
	buttonCompound: {
		height: 24,
		minWidth: 90,
	},
	buttonStack: {
		display: 'flex',
		gap: 8,
		flexDirection: 'column',
		whiteSpace: 'nowrap',
		marginTop: 12,
	},
	container: {
		...cardStyle,
		overflow: 'visible',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		border: `1px solid ${inputBorderColor}`,
		boxShadow: shadowSm,
		...CssSelectors.allDescendants(
			{
				border: `1px dashed ${brandSecondary}`,
				borderRadius,
				boxSizing: 'border-box',
				fontSize: 14,
				padding: xs,
				marginLeft: xs,
				marginTop: -xs,
				display: 'inline-block',
			},
			'span[data-placeholder="true"]'
		),
		fontSize: 14,
		padding: 20,
	},
	featured: {
		background: 'linear-gradient(156.83deg, #F5FAFD 14.57%, #E8F3F9 42%, #E8F3F9 85.02%)',
	},
	flagHighlight: {
		marginTop: 50,
	},
	headerContainer: {
		display: 'flex',
		justifyContent: 'space-between',
		paddingBottom: 2,
	},
	menu: {
		position: 'absolute',
		right: 5,
		top: 5,
		width: 30,
		color: aceGray,
		fontSize: 20,
		marginLeft: 'auto',
	},
	menuItem: {
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		fontSize: 14,
		padding: '4px 16px',
		textAlign: 'center',
		width: '100%',
		':hover': {
			background: '#F3F8FB',
		},
	},
	nameContainer: {
		boxSizing: 'border-box',
		color: titles,
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		width: '100%',
	},
	newItem: {
		boxSizing: 'border-box',
		backgroundColor: brandPrimary,
		color: white,
		fontSize: 10,
		fontWeight: 600,
		padding: `4px 8px`,
		boxShadow: shadowMd,
		borderRadius: 'calc(infinity * 1px)',
	},
	sharedItem: {
		color: brandSecondary,
		fontSize: '12px',
		fontStyle: 'italic',
		marginLeft: 8,
	},
	sharedByFooter: {
		color: THEME.colors.text.light,
		fontSize: '11px',
		fontStyle: 'italic',
		textAlign: 'center',
		padding: '8px 15px 0px 15px',
		marginTop: 8,
	},
	noPaddingTop: {
		paddingTop: 0,
		marginTop: 15,
	},
	openLetter: {
		height: 82,
		position: 'absolute',
		right: -10,
		top: -10,
		transform: 'translate3d(0px, -10px, 0)',
	},
	popover: {
		fontSize: 14,
	},
	popoverAnchor: {
		zIndex: 5,
	},
	popoverContainer: {
		marginRight: 25,
	},
	reviewed: {
		color: success,
	},
	reviewedStack: {
		...Layouts.horizontalStack(1),
		alignItems: 'center',
		justifyContent: 'flex-end',
		color: gray3,
		display: 'flex',
		fontSize: 11,
	},
	sendNow: {
		height: 82,
		left: -8,
		position: 'absolute',
		top: -16,
	},
	sharedBy: {
		fontSize: 11,
		fontStyle: 'italic',
		color: `${midGrey} !important`,
		opacity: 0.8,
	},
	subject: {
		color: brandPrimaryText,
		fontSize: 14,
		fontWeight: 'bold',
		marginBottom: 15,
		padding: 0,
		wordBreak: 'break-all',
	},
	socialMediaTag: {
		boxSizing: 'border-box',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 'calc(infinity * 1px)',
		padding: '4px 8px',
		fontSize: 10,
		width: 'fit-content',
		whitespace: 'nowrap',
		flexShrink: 0,
		gap: 4,
		overflow: 'hidden',
		color: brandPrimary,
		border: `1px solid ${brandPrimary}`,
		':has(*) > svg': {
			display: 'block',
			pointerEvents: 'none',
			width: 10,
			height: 10,
		},
		':hover': {
			borderColor: brandPrimaryActive,
			color: brandPrimaryActive,
		},
	},
	tagButtonContent: {
		display: 'flex',
		flexDirection: 'row',
	},
	tagButtonContentCenter: {
		alignItems: 'center',
	},
	textContainer: {
		height: 80,
		marginTop: 12,
	},
	textContent: {
		color: `${darkGrayFontColor} !important`,
		fontFamily: 'Sora !important',
		fontSize: '12px !important',
		marginBottom: 0,
		marginTop: 0,
		paddingBottom: 0,
		lineHeight: '1.5',
		margin: 0,
		width: '100%',
		boxSizing: 'border-box',
	},
	textContentSmall: {
		fontSize: '12px !important',
		height: 'auto',
	},
	// Caps the preview at the three lines the card was clipped to before, so a long preview on a narrow card cannot
	// push the grid rows out of alignment.
	textContentThreeLines: {
		display: '-webkit-box' as any,
		'-webkit-line-clamp': '3' as any,
		'-webkit-box-orient': 'vertical' as any,
		overflow: 'hidden',
	},
	// The sanitized preview markup carries no attributes, so every declaration it needs has to come from here:
	// without the size override a heading renders at its browser default, and without the margin reset paragraphs
	// gain a 1em gap that email markup used to suppress inline.
	previewHtml: {
		display: '-webkit-box' as any,
		'-webkit-line-clamp': '3' as any,
		'-webkit-box-orient': 'vertical' as any,
		overflow: 'hidden',
		paddingTop: xs,
		color: darkGrayFontColor,
		fontFamily: 'Sora',
		fontSize: 12,
		':has(*) img': {
			display: 'none',
		},
		':has(*) *': {
			fontFamily: 'Sora !important',
			fontSize: `12px !important`,
			background: 'transparent !important',
			color: `${darkGrayFontColor} !important`,
			margin: '0 !important',
		},
		':has(*) [data-placeholder="true"]': {
			marginTop: 0,
		},
	},
	textContentTwoLines: {
		display: '-webkit-box' as any,
		'-webkit-line-clamp': '2' as any,
		'-webkit-box-orient': 'vertical' as any,
		overflow: 'hidden',
		//textOverflow: 'ellipsis',
		fontSize: 14,
		fontWeight: 600,
		margin: 0,
		minHeight: 32,
		cursor: 'pointer',
	},
	userName: {
		display: 'inline-flex',
		fontSize: 11,
		lineHeight: '15px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	alignCenter: {
		alignItems: 'center',
	},
	approvalText: {
		display: 'flex',
		justifyContent: 'flex-end',
	},
	userGroupsContainer: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: 4,
		marginTop: 8,
	},
	userGroupsLabel: {
		fontSize: 11,
		color: THEME.colors.text.light,
		marginRight: 2,
		flexShrink: 0,
	},
});
